import React, { useState } from 'react';
import { FaShoppingCart, FaChartLine, FaBoxes, FaHandshake, FaUsers, FaMobile, FaClipboardCheck, FaClock } from 'react-icons/fa';

const WhatWeOffer = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  
  const services = [
    {
      id: 1,
      icon: <FaShoppingCart className="text-4xl text-blue-600" />,
      title: "Point-of-Sale",
      description: "Streamline your pharmacy's transaction process, making sales quicker, more accurate, and more efficient. With a user-friendly interface and seamless integration with inventory and customer data.",
      features: ["Barcode scanning", "Multiple payment options", "Fast checkout", "Receipt customization"]
    },
    {
      id: 2,
      icon: <FaChartLine className="text-4xl text-blue-600" />,
      title: "Reporting & Analytics",
      description: "Stay ahead of the competition and make data-driven decisions with comprehensive analytics tools. From sales trends to operational efficiency, gain the insights you need to optimize every aspect of your pharmacy.",
      features: ["Custom dashboards", "Automated reports", "Trend analysis", "Export capabilities"]
    },
    {
      id: 3,
      icon: <FaBoxes className="text-4xl text-blue-600" />,
      title: "Inventory Management",
      description: "Gain powerful tools to track, manage, and optimize your inventory, ensuring you always have the right stock at the right time. Monitor expiration dates, set reorder points, and eliminate stockouts.",
      features: ["Expiry tracking", "Automated reordering", "Batch tracking", "Real-time updates"]
    },
    {
      id: 4,
      icon: <FaHandshake className="text-4xl text-blue-600" />,
      title: "Supplier Management",
      description: "Streamline your supplier relationships and ensure your inventory is always on point. Compare prices, track orders, and maintain clear communication with all your suppliers in one place.",
      features: ["Vendor comparison", "Order history", "Performance metrics", "Automated ordering"]
    },
    {
      id: 5,
      icon: <FaUsers className="text-4xl text-blue-600" />,
      title: "Patient Management",
      description: "Build stronger relationships with your patients through comprehensive profiles, medication histories, and automated refill reminders that keep them coming back to your pharmacy.",
      features: ["Medical history", "Prescription tracking", "Insurance management", "Communication tools"]
    },
    {
      id: 6,
      icon: <FaMobile className="text-4xl text-blue-600" />,
      title: "Mobile Access",
      description: "Access your pharmacy system on the go with our mobile-friendly platform. Check inventory, process orders, and view reports from anywhere, ensuring you're always connected to your business.",
      features: ["iOS & Android apps", "Responsive design", "Offline capabilities", "Secure access"]
    },
    {
      id: 7,
      icon: <FaClipboardCheck className="text-4xl text-blue-600" />,
      title: "Compliance Tools",
      description: "Stay compliant with industry regulations and standards with our built-in compliance tools that keep your pharmacy operating within legal guidelines with minimal effort.",
      features: ["HIPAA compliance", "Audit trails", "Regulatory updates", "Documentation"]
    },
    {
      id: 8,
      icon: <FaClock className="text-4xl text-blue-600" />,
      title: "Scheduling System",
      description: "Manage staff schedules, patient appointments, and medication pickups with our integrated scheduling system that keeps every aspect of your pharmacy running on time.",
      features: ["Staff scheduling", "Patient appointments", "Reminder system", "Calendar integration"]
    }
  ];

  return (
    <div className="py-24 relative overflow-hidden" id="services">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white z-0"></div>
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-blue-100 opacity-20 blur-3xl"></div>
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-teal-100 opacity-30 blur-3xl"></div>
      
      {/* Content container */}
      <div className="container mx-auto px-5 relative z-10">
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center py-2 px-4 bg-blue-50 border border-blue-100 rounded-full shadow-sm mb-4">
            <div className="h-2 w-2 rounded-full bg-blue-500 mr-2"></div>
            <p className="text-sm font-medium text-gray-600">Our Services</p>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6">
            Dedicated to <span className="text-blue-600 relative">
              Pharmacy
              <span className="absolute -bottom-2 left-0 h-1 w-full bg-blue-400 opacity-50 rounded-full"></span>
            </span> Management
          </h2>
          
          <p className="max-w-2xl mx-auto text-gray-600">
            Discover our comprehensive suite of pharmacy management solutions designed to streamline operations,
            improve patient care, and boost your pharmacy's performance.
          </p>
        </div>
        
        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
              onMouseEnter={() => setHoveredCard(service.id)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                transform: hoveredCard === service.id ? 'translateY(-8px)' : 'translateY(0)',
                transition: 'transform 0.3s ease'
              }}
            >
              <div className="p-6">
                {/* Icon with gradient background */}
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center mb-5">
                  {service.icon}
                </div>
                
                <h3 className="text-xl font-bold text-gray-800 mb-3">{service.title}</h3>
                <p className="text-gray-600 text-sm mb-5">{service.description}</p>
                
                {/* Features list */}
                <ul className="space-y-2 mt-4">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-sm text-gray-700">
                      <span className="w-2 h-2 rounded-full bg-blue-500 mr-2"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Bottom gradient bar */}
              <div className="h-1 w-full bg-gradient-to-r from-blue-400 to-teal-400 transform origin-left transition-transform duration-300 scale-x-0 group-hover:scale-x-100"></div>
            </div>
          ))}
        </div>
        
        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="max-w-3xl mx-auto bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 md:p-10 shadow-lg">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to transform your pharmacy?</h3>
            <p className="text-blue-50 mb-8">Get started with RxUltimate today and experience the difference our complete pharmacy management system can make.</p>
            <a href="#join-us">
              <button className="bg-white text-blue-600 hover:bg-blue-50 font-medium px-8 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-300">
                Schedule a Demo
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatWeOffer;